import MarkdownToJsx, { type MarkdownToJSX } from 'markdown-to-jsx';
import type { ExtendWithCommonProps } from '../../types';
import { Link } from '../link/link';
import { List } from '../list/list';
import { Text, type TextProps } from '../text/text';
import scss from './markdown.module.scss';

export type MarkdownProps = ExtendWithCommonProps<
  Omit<MarkdownToJSX.Options, 'wrapper'>,
  {
    /** HTML tag to render */
    as?: 'article' | 'div' | 'section';
    variant?: 'p-100' | 'lgl-100';
    children: string;
  }
>;

export const defaultMarkdownOverrides = {
  h1: {
    component: Text,
    props: {
      variant: 'hl-300',
      as: 'h1',
      margin: { bottom: 600 },
      className: scss.h1,
    } as TextProps,
  },
  h2: {
    component: Text,
    props: {
      as: 'h2',
      variant: 'hl-200',
      margin: { bottom: 500 },
      className: scss.h2,
    },
  },
  h3: {
    component: Text,
    props: {
      variant: 'hl-200',
      as: 'h3',
      margin: { bottom: 500 },
      className: scss.h3,
    },
  },
  h4: {
    component: Text,
    props: {
      variant: 'hl-100',
      as: 'h4',
      margin: { bottom: 400 },
      className: scss.h4,
    },
  },
  h5: {
    component: Text,
    props: {
      variant: 'hl-50',
      as: 'h5',
      margin: { bottom: 300 },
      className: scss.h5,
    },
  },
  h6: {
    component: Text,
    props: {
      variant: 'hl-50',
      as: 'h6',
      margin: { bottom: 300 },
      className: scss.h6,
    },
  },
  blockquote: {
    component: Text,
    props: {
      variant: 'lgl-100',
    },
  },
  code: {
    component: Text,
    props: {
      variant: 'lgl-100',
    },
  },
  pre: {
    component: Text,
    props: {
      variant: 'lgl-100',
    },
  },
  p: {
    component: Text,
    props: {
      variant: 'p-100',
      as: 'p',
      className: scss.p,
    },
  },
  a: {
    component: Link,
    props: {
      inline: true,
      variant: 'link-100',
    },
  },
  ul: {
    component: List,
    props: {
      as: 'ul',
    },
  },
  ol: {
    component: List,
    props: {
      as: 'ol',
    },
  },
} as const;

export const defaultMarkdownLegalOverrides = {
  ...defaultMarkdownOverrides,
  p: {
    component: Text,
    props: {
      variant: 'lgl-100',
      as: 'p',
      className: scss.p,
    },
  },
  a: {
    component: Link,
    props: {
      inline: true,
      variant: 'hyp-lgl',
    },
  },
} as const;

/**
 * Use to render markdown content as HTML.
 * A list of overrides can be provided to customize the rendering of specific markdown elements.
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/components-markdown--docs
 */
export const Markdown = ({
  as = 'div',
  children: text,
  variant = 'p-100',
  className,
  ...props
}: MarkdownProps) => {
  const isString = typeof text === 'string';
  if (!isString) console.warn('Markdown recieved is not a string', text);
  let children = isString ? text : '';

  children = children.replace(/\\([.?*+[\]{}()^$|\\])/gi, '$1').replace(
    /\\&nbsp;/gi,
    // eslint-disable-next-line no-useless-escape
    `\  
    `,
  );

  const overrides = {
    ...defaultMarkdownOverrides,
    ...(variant === 'lgl-100' ? defaultMarkdownLegalOverrides : {}),
    ...(props.overrides ?? {}),
  };

  return (
    <MarkdownToJsx
      options={{
        wrapper: as,
        overrides,
        ...props,
      }}
      className={className}
    >
      {children}
    </MarkdownToJsx>
  );
};

Markdown.displayName = 'Markdown';
